// Voice Mode 2.0 — P1: raw PCM helpers.
//
// The live loop's wire format is what Piper emits and the browser can play with
// zero transcoding: **int16 mono PCM** at the session sample rate (22050 Hz for
// `en_US-lessac-medium`). Everything here is pure and unit-tested.

export const INT16_MAX = 32767;
export const INT16_MIN = -32768;

/** Float samples (-1..1) → clamped int16 PCM. */
export function floatToInt16Pcm(samples: Float32Array): Int16Array {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i] ?? 0));
    out[i] = s < 0 ? s * -INT16_MIN : s * INT16_MAX;
  }
  return out;
}

/** int16 PCM → float samples (-1..1). */
export function pcm16ToFloat32(pcm: Int16Array): Float32Array {
  const out = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i += 1) {
    out[i] = Math.max(-1, Math.min(1, pcm[i] / INT16_MAX));
  }
  return out;
}

/**
 * Raw socket payload → int16 samples.
 * A trailing odd byte (misaligned frame) is dropped rather than crashing.
 */
export function bytesToInt16(data: ArrayBuffer | ArrayBufferView): Int16Array {
  const view =
    data instanceof ArrayBuffer
      ? new Uint8Array(data)
      : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  const usable = view.length - (view.length % 2);
  if (usable <= 0) return new Int16Array(0);
  // Copy into an aligned buffer: `subarray` offsets can be odd on the wire.
  const aligned = new Uint8Array(usable);
  aligned.set(view.subarray(0, usable));
  return new Int16Array(aligned.buffer);
}

/** Root-mean-square level (0..1) — drives the VAD and the UI level meter. */
export function rms(samples: Float32Array): number {
  if (!samples.length) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i];
  return Math.sqrt(sum / samples.length);
}

export function isSilent(samples: Float32Array, threshold = 0.012): boolean {
  return rms(samples) < threshold;
}

/** Seconds of audio in `length` int16 samples. */
export function pcmDurationSeconds(length: number, sampleRate: number): number {
  if (!sampleRate) return 0;
  return length / sampleRate;
}
